import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { getErrorMessage } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/ToastProvider';
import { useConfirm } from '../components/ConfirmProvider';
import StateBlock, { Spinner } from '../components/StateBlock';
import PasswordInput from '../components/PasswordInput';

const EMPTY_PASSWORDS = { senhaAtual: '', novaSenha: '', confirmarNovaSenha: '' };

const Perfil = () => {
  const { authData, updateUserName, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();

  const [userData, setUserData] = useState({ nome: '', email: '' });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [passwords, setPasswords] = useState(EMPTY_PASSWORDS);
  const [isSavingName, setIsSavingName] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchProfile = useCallback(async () => {
    setLoading(true);

    try {
      const { data } = await api.get('/users/profile');
      setUserData({ nome: data.nome || '', email: data.email || '' });
      setNewName(data.nome || '');
      setLoadError('');
    } catch (error) {
      setLoadError(getErrorMessage(error, 'Não foi possível carregar o perfil.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchProfile();
    }
  }, [fetchProfile, isAuthenticated]);

  const handleNameSubmit = async (event) => {
    event.preventDefault();
    const nome = newName.trim();

    if (!nome) {
      toast.error('O nome não pode ficar vazio.');
      return;
    }

    setIsSavingName(true);

    try {
      const { data } = await api.put('/users/update-name', { nome });
      setUserData((prev) => ({ ...prev, nome: data.nome }));
      updateUserName(data.nome);
      setEditingName(false);
      toast.success('Nome atualizado.');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Não foi possível atualizar o nome.'));
    } finally {
      setIsSavingName(false);
    }
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    const { senhaAtual, novaSenha, confirmarNovaSenha } = passwords;

    if (!senhaAtual || !novaSenha || !confirmarNovaSenha) {
      toast.error('Preencha todos os campos de senha.');
      return;
    }

    if (novaSenha !== confirmarNovaSenha) {
      toast.error('A nova senha e a confirmação não coincidem.');
      return;
    }

    if (novaSenha === senhaAtual) {
      toast.error('A nova senha precisa ser diferente da atual.');
      return;
    }

    setIsSavingPassword(true);

    try {
      const { data } = await api.put('/users/update-password', { senhaAtual, novaSenha });
      setPasswords(EMPTY_PASSWORDS);
      toast.success(data?.msg || 'Senha atualizada.');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Não foi possível atualizar a senha.'));
    } finally {
      setIsSavingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    const confirmed = await confirm({
      title: 'Excluir sua conta?',
      message:
        'Esta ação é irreversível: o usuário e todas as listas criadas por você serão apagados.',
      confirmLabel: 'Excluir minha conta',
      tone: 'danger',
    });

    if (!confirmed) {
      return;
    }

    setIsDeleting(true);

    try {
      await api.delete('/users/delete-account');
      logout();
      navigate('/', { replace: true });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Não foi possível excluir a conta.'));
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="page">
        <StateBlock busy title="Carregando perfil..." />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="page">
        <StateBlock icon="bi-wifi-off" title="Não foi possível carregar" description={loadError}>
          <button type="button" className="btn btn--primary" onClick={fetchProfile}>
            Tentar novamente
          </button>
        </StateBlock>
      </div>
    );
  }

  const initial = (userData.nome || authData.userName || 'U').charAt(0).toUpperCase();

  return (
    <div className="page">
      <section className="surface-card card-pad profile-hero">
        <div className="profile-identity">
          <span className="avatar" aria-hidden="true">
            {initial}
          </span>
          <div className="profile-identity__text">
            <span className="eyebrow">Perfil</span>
            <h1>{userData.nome || 'Sua conta'}</h1>
            <p>{userData.email}</p>
          </div>
        </div>

        <div className="metric-grid">
          <div className="metric">
            <span className="metric__label">Conta</span>
            <strong className="metric__value">Ativa</strong>
          </div>
          <div className="metric">
            <span className="metric__label">Sessão</span>
            <strong className="metric__value">Neste aparelho</strong>
          </div>
        </div>
      </section>

      <div className="profile-grid">
        <section className="surface-card card-pad">
          <div className="section-heading">
            <div className="section-heading__text">
              <span className="eyebrow">Dados</span>
              <h2 className="section-subtitle">Informações da conta</h2>
            </div>
          </div>

          <div className="stack">
            {editingName ? (
              <form className="inline-panel" onSubmit={handleNameSubmit}>
                <div className="field">
                  <label className="field-label" htmlFor="perfil-nome">
                    Nome
                  </label>
                  <input
                    id="perfil-nome"
                    type="text"
                    className="input"
                    value={newName}
                    autoFocus
                    onChange={(event) => setNewName(event.target.value)}
                  />
                </div>
                <div className="btn-row btn-row--end">
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => {
                      setEditingName(false);
                      setNewName(userData.nome);
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn--primary btn--sm"
                    disabled={isSavingName}
                  >
                    {isSavingName ? <Spinner label="Salvando..." /> : 'Salvar'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="data-row">
                <div>
                  <span className="data-row__label">Nome</span>
                  <strong>{userData.nome}</strong>
                </div>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => setEditingName(true)}
                >
                  <i className="bi bi-pencil" aria-hidden="true"></i>
                  Editar
                </button>
              </div>
            )}

            <div className="data-row">
              <div>
                <span className="data-row__label">E-mail</span>
                <strong>{userData.email}</strong>
              </div>
            </div>
          </div>
        </section>

        <section className="surface-card card-pad">
          <div className="section-heading">
            <div className="section-heading__text">
              <span className="eyebrow">Segurança</span>
              <h2 className="section-subtitle">Alterar senha</h2>
            </div>
          </div>

          <form onSubmit={handlePasswordSubmit} className="field-form">
            <input
              type="hidden"
              name="username"
              value={userData.email}
              autoComplete="username"
              readOnly
            />

            <PasswordInput
              id="senha-atual"
              label="Senha atual"
              value={passwords.senhaAtual}
              onChange={(event) =>
                setPasswords((prev) => ({ ...prev, senhaAtual: event.target.value }))
              }
              autoComplete="current-password"
            />

            <PasswordInput
              id="nova-senha"
              label="Nova senha"
              value={passwords.novaSenha}
              onChange={(event) =>
                setPasswords((prev) => ({ ...prev, novaSenha: event.target.value }))
              }
              autoComplete="new-password"
            />

            <PasswordInput
              id="confirmar-nova-senha"
              label="Confirmar nova senha"
              value={passwords.confirmarNovaSenha}
              onChange={(event) =>
                setPasswords((prev) => ({ ...prev, confirmarNovaSenha: event.target.value }))
              }
              autoComplete="new-password"
            />

            <button
              type="submit"
              className="btn btn--primary btn--block"
              disabled={isSavingPassword}
            >
              {isSavingPassword ? <Spinner label="Atualizando..." /> : 'Atualizar senha'}
            </button>
          </form>
        </section>
      </div>

      <section className="surface-card card-pad danger-zone">
        <div>
          <span className="eyebrow">Zona de risco</span>
          <h2 className="section-subtitle">Excluir conta</h2>
          <p className="section-copy">
            Remove seu usuário e todas as listas criadas por você, de forma permanente.
          </p>
        </div>
        <button
          type="button"
          className="btn btn--danger"
          onClick={handleDeleteAccount}
          disabled={isDeleting}
        >
          {isDeleting ? <Spinner label="Excluindo..." /> : 'Excluir conta'}
        </button>
      </section>
    </div>
  );
};

export default Perfil;
